import React from 'react';
import { Search, X, Filter, HelpCircle } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { MultiSelect, Option } from '../../../../components/ui/multi-select';
import { PROJECT_TYPES, STATUS_TYPES } from '../../constants/filterConstants';
import { Button } from '../../../../components/ui/button';
import { Tooltip } from '../../../../components/ui/tooltip';

interface ProjectFilters {
  status: string[];
  type: string[];
  customer: string[];
}

interface ProjectFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filters: ProjectFilters;
  onFilterChange: (key: keyof ProjectFilters) => (value: string[]) => void;
  uniqueCustomers: string[];
  hasActiveFilters: boolean;
  filteredCount: number;
  onResetFilters: () => void;
}

export const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  uniqueCustomers,
  hasActiveFilters,
  filteredCount,
  onResetFilters,
}) => {
  // Convert arrays to options for MultiSelect
  const statusOptions: Option[] = STATUS_TYPES.map(status => ({
    value: status,
    label: status
  }));

  const typeOptions: Option[] = PROJECT_TYPES.map(type => ({
    value: type,
    label: type
  }));

  const customerOptions: Option[] = uniqueCustomers.map(customer => ({
    value: customer,
    label: customer
  }));

  return (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4 h-8">
        <div className="flex items-center gap-2 justify-center">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="text-[14px] font-semibold text-blueprint-blue whitespace-nowrap">Filters ({filteredCount } projects)</span>
        </div>
        <div className="h-8 flex items-center justify-center">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetFilters}
              className="text-gray-500 hover:text-gray-700 transition-opacity duration-200"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>
      
      {/* Single Row Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Input
            placeholder="Search projects by name, number, location, or customer."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pr-8"
          />
        </div>
        <div className="w-[200px]">
          <MultiSelect
            options={statusOptions}
            selected={filters.status}
            onChange={onFilterChange('status')}
            placeholder="Filter by Status"
            className="w-full"
            isSearchable={false}
          />
        </div>
        <div className="w-[200px]">
          <MultiSelect
            options={typeOptions}
            selected={filters.type}
            onChange={onFilterChange('type')}
            placeholder="Filter by Type"
            className="w-full"
            isSearchable={false}
          />
        </div>
        <div className="w-[250px]">
          <MultiSelect
            options={customerOptions}
            selected={filters.customer}
            onChange={onFilterChange('customer')}
            placeholder="Filter by Customer"
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
}; 